import React, { FC, useEffect, useState } from 'react';
import { useLocation } from 'react-router';

import { useI18n, useLogger, useSelector } from '~Hooks';
import IconButton from '~ComponentsImproved/IconButton';
import SystemIcon from '~Assets/icons/sync.svg';
import Icon from '~ComponentsImproved/Icon';
import useModal from '~ComponentsImproved/Modal/useModal';
import Heading from '~ComponentsImproved/Typography/Heading';
import { Box } from '~Styles';
import Modal from '~ComponentsImproved/Modal';
import styled from '@emotion/styled';
import Paragraph from '~ComponentsImproved/Typography/Paragraph';
import CheckCircleIcon from '~Assets/icons/check-circle.svg';
import WarningIcon from '~Assets/icons/warning.svg';
import ErrorRow from './ErrorRow';
import InternetStatus from '~Components/InternetStatus';
import _ from 'lodash';
import HighEventVolume from './HighEventVolume';

const Content = styled(Box)`
  position: absolute;
  top: 0;
  left: 0;
  bottom: 0;
  padding: 32px 16px;
  width: 448px;
  background-color: var(--colorBlueGray100);
  overflow: auto;
  box-shadow: 16px 0px 32px 0px rgb(0 0 0 / 32%);
`;

export const STATUS_COLORS = {
  OK: 'var(--colorUIGreen)',
  WARNING: 'var(--colorUIYellow)',
  ERROR: 'var(--colorUIRed)',
};

const STATUS_ICONS = {
  OK: CheckCircleIcon,
  WARNING: WarningIcon,
  ERROR: WarningIcon,
};

export type StatusType = 'OK' | 'WARNING' | 'ERROR';

const ROUTES_TO_HIDE = ['customer-display', 'express'];

interface Props {
  hideBorder?: boolean;
  blinking?: boolean;
}

/** Displays in a slide menu if there has been any system errors - Can for instance be if an order wasn't synced */
const SystemStatus: FC<Props> = ({ hideBorder, blinking }) => {
  const { i18n } = useI18n();

  const logger = useLogger('system-status');
  const location = useLocation();
  const showStatusModal = useModal();

  const errors = useSelector((state) => state.errors);
  const orderStatusEvents = useSelector((state) => state.orderStatusEvents);
  const tableEvents = useSelector((state) => state.tableEvents);

  const [status, setStatus] = useState<StatusType>('OK');
  const [ignoredHighEventWarnings, setIgnoredHighEventWarnings] = useState<Set<string>>(new Set());

  useEffect(() => {
    if (errors.length) {
      setStatus('ERROR');
      return;
    }

    /** Some customers have reported slowdowns during super-high-pressure events, most likely caused by a lot of events locally being replayed */
    const hasOrderStatusWarning =
      orderStatusEvents.length > 6000 && !ignoredHighEventWarnings.has('orderStatusEvents');
    const hasTableEventsWarning =
      tableEvents.length > 7000 && !ignoredHighEventWarnings.has('tableEvents');

    if (hasOrderStatusWarning || hasTableEventsWarning) {
      setStatus('WARNING');
      return;
    }

    setStatus('OK');
  }, [errors, orderStatusEvents.length, tableEvents.length, ignoredHighEventWarnings]);

  if (ROUTES_TO_HIDE.includes(location.pathname)) {
    return null;
  }

  const statusHeading = {
    OK: i18n('SystemStatus.Operational'),
    WARNING: i18n('SystemStatus.Warning'),
    ERROR: i18n('SystemStatus.ExperiencingIssues'),
  };

  const buttonText = {
    OK: i18n('SystemStatus.NoErrors'),
    WARNING: i18n('SystemStatus.Warning.CTA'),
    ERROR: `${errors.length} ${i18n('SystemStatus.Errors').toLowerCase()}`,
  };

  const styles = {
    // Set border none if hideBorder is true
    ...(hideBorder ? { borderTop: 'none' } : {}),
  };

  return (
    <>
      <IconButton
        background={
          blinking && status === 'WARNING'
            ? {
                color: 'var(--colorUIOrange)',
                blinking: true,
              }
            : undefined
        }
        icon={
          <Icon
            src={status === 'WARNING' ? WarningIcon : SystemIcon}
            color={status === 'OK' ? 'var(--colorBlueGray600)' : STATUS_COLORS[status]}
          />
        }
        text={buttonText[status]}
        onClick={showStatusModal.toggle}
        styles={styles}
      />

      <Modal isShowing={showStatusModal.isShowing} hide={showStatusModal.hide}>
        <Content display="flex" flexDirection="column" gap="small">
          <Box
            display="flex"
            justifyContent="center"
            flexDirection="column"
            alignItems="center"
            gap="small"
            textAlign="center">
            <Icon
              src={STATUS_ICONS[status]}
              color={STATUS_COLORS[status]}
              height="80px"
              width="80px"
            />
            <Box display="flex" flexDirection="column" gap="xs">
              <Heading level={1}>{statusHeading[status]}</Heading>
              {status === 'OK' && (
                <Paragraph level={3}>{i18n('SystemStatus.Operational.Desc')}</Paragraph>
              )}
            </Box>
          </Box>

          <InternetStatus />

          {orderStatusEvents.length > 6000 && (
            <HighEventVolume
              type="orderStatusEvents"
              eventsQuantity={orderStatusEvents.length}
              onIgnore={(type) => {
                setIgnoredHighEventWarnings((prev) => new Set(prev).add(type));
                logger.info('User ignored high event volume warning', { type });
              }}
            />
          )}

          {tableEvents.length > 8000 && (
            <HighEventVolume
              type="tableEvents"
              eventsQuantity={tableEvents.length}
              onIgnore={(type) => {
                setIgnoredHighEventWarnings((prev) => new Set(prev).add(type));
                logger.info('User ignored high event volume warning', { type });
              }}
            />
          )}

          {errors.length > 0 && (
            <Box display="flex" flexDirection="column" gap="xs">
              <Paragraph level={4}>{_.capitalize(i18n('SystemStatus.Errors'))}:</Paragraph>
              {errors.map((e) => (
                <ErrorRow key={e.id} error={e} />
              ))}
            </Box>
          )}
        </Content>
      </Modal>
    </>
  );
};

export default React.memo(SystemStatus);
